/**
 * API key helpers for client-side fallback/overrides.
 * Global shared keys for all users are resolved on the server proxy.
 */

const GEMINI_ARRAY_STORAGE_KEY = "ld_gemini";
const GEMINI_LEGACY_STORAGE_KEY = "gemini_api_key";
const POLLINATIONS_ARRAY_STORAGE_KEY = "ld_pollinations";
const POLLINATIONS_LEGACY_STORAGE_KEY = "pollinations_api_key";

export function parseStoredKeys(value: string | null): string[] {
  if (!value?.trim()) return [];
  const trimmed = value.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((item) => item.trim())
        : [];
    } catch {
      return [trimmed];
    }
  }
  return [trimmed];
}

export function normalizeApiKeys(keys: string | string[] | null | undefined): string[] {
  const rawValues = Array.isArray(keys) ? keys : keys ? [keys] : [];
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const raw of rawValues) {
    if (typeof raw !== "string") continue;
    const key = raw.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }

  return normalized;
}

export function mergeApiKeys(...sources: Array<string | string[] | null | undefined>): string[] {
  return normalizeApiKeys(
    sources.flatMap((source) => {
      if (Array.isArray(source)) return source;
      return source ? [source] : [];
    })
  );
}

function getKeysFromLocalStorage(arrayKey: string, legacyKey: string): string[] {
  if (typeof localStorage === "undefined") return [];
  return mergeApiKeys(
    parseStoredKeys(localStorage.getItem(arrayKey)),
    parseStoredKeys(localStorage.getItem(legacyKey))
  );
}

export function getStoredGeminiKeys(): string[] {
  return mergeApiKeys(
    getKeysFromLocalStorage(GEMINI_ARRAY_STORAGE_KEY, GEMINI_LEGACY_STORAGE_KEY),
    (import.meta.env.VITE_GEMINI_API_KEY as string | undefined)?.trim(),
    (import.meta.env.GEMINI_API_KEY as string | undefined)?.trim()
  );
}

export function getStoredPollinationsKeys(): string[] {
  return mergeApiKeys(
    getKeysFromLocalStorage(POLLINATIONS_ARRAY_STORAGE_KEY, POLLINATIONS_LEGACY_STORAGE_KEY),
    (import.meta.env.VITE_POLLINATIONS_API_KEY as string | undefined)?.trim()
  );
}

